import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from './Button';
import { Modal } from './Modal';

interface ConfirmOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'danger' | 'primary';
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    setOpts(options);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  function done(value: boolean) {
    resolver.current?.(value);
    resolver.current = null;
    setOpts(null);
  }

  const value = useMemo(() => confirm, [confirm]);
  const isDanger = opts?.tone !== 'primary';

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <Modal
        open={!!opts}
        onClose={() => done(false)}
        title={
          <span className="flex items-center gap-3">
            <span
              className={
                isDanger
                  ? 'flex size-9 items-center justify-center rounded-xl bg-danger-soft text-danger'
                  : 'flex size-9 items-center justify-center rounded-xl bg-brand-100/60 text-brand-600'
              }
            >
              <AlertTriangle className="size-4" />
            </span>
            <span>{opts?.title}</span>
          </span>
        }
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => done(false)}>
              {opts?.cancelLabel ?? 'Cancelar'}
            </Button>
            <Button
              variant={opts?.tone === 'primary' ? 'primary' : 'danger'}
              onClick={() => done(true)}
            >
              {opts?.confirmLabel ?? 'Confirmar'}
            </Button>
          </>
        }
      >
        <div className="text-sm text-ink-muted leading-relaxed">
          {opts?.description ?? ''}
        </div>
      </Modal>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm deve ser usado dentro de ConfirmProvider');
  return ctx;
}
